<template>
  <div v-if="shouldShow" class="filter-group">
    <a class="filter-group-heading" @click.prevent="toggleCollapse">
      <h4 class="filter-group-heading-text no-transform">{{ title }}</h4>
      <span v-if="allowCollapse && collapsed" class="filter-group-heading-plus"><i class="af af-plus"></i></span>
      <span v-if="allowCollapse && !collapsed" class="filter-group-heading-minus"><i class="af af-minus"></i></span>
    </a>
    <div ref="filterGroupOptions" class="filter-group-options-container" :class="[!collapsed && 'product-values-expanded']">
      <div class="filter-group-options" :class="{ 'filter-group-options-collapsed': allowCollapse && collapsed }">
        <slot name="options">
          <template v-for="option in filterOptions" :key="`filter-group-checkbox-${option.id}`">
            <checkbox
              v-model="selection[option.id]"
              :title="option.userFriendlyName || option.name"
              :color="option.hex"
              :circle="!option.hex"
              :disabled="(selection[option.id] ? false : optionsDisabled) ? optionsDisabled.indexOf(option.id) > -1 : false"
              :class="[`filter-${option.id}`]"
              @update:modelValue="filterChanged($event, option.id)"
            ></checkbox>
          </template>
        </slot>
        <a v-if="allowExpandValues" href="" class="expand-options checkbox" @click.prevent="onProductValuesExpandClick">
          <span class="af" :class="[expanded ? 'af-chevron-up' : 'af-chevron-down']"></span><span class="text">All {{ getPluralized(title) }}</span>
        </a>
      </div>
    </div>
  </div>
</template>
<script>
import { Checkbox } from '@/pixel/index';

import smoothReflow from '@/plugins/smoothReflow';
import { naivePluralize } from '@/components/commons/utils';

// Number of items used as a buffer for deciding if a group should be collapsed.
// eg. if expandCount is 10 and there are 11 items, it doesn't make sense to collapse
// the section, so an allowance of 3 allows for 13 items before collapsing.
const EXPAND_COLLAPSE_ALLOWANCE = 3;

export default {
  components: {
    checkbox: Checkbox,
  },

  mixins: [smoothReflow],

  props: {
    options: {
      type: Array,
      default: () => [],
    },
    modelValue: {
      type: Object,
      default: () => {},
    },
    title: {
      type: String,
      required: false,
    },
    allowCollapse: {
      type: Boolean,
      default: false,
    },
    defaultExpanded: {
      type: Boolean,
      default: false,
    },
    allowExpand: {
      type: Boolean,
      default: true,
    },
    expandCount: {
      type: Number,
      default: 10
    },
    isCustom: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      collapsed: this.defaultExpanded
        ? false
        : (!this.modelValue ||
            !Object.values(this.modelValue).filter((value) => this.options.filter((option) => option.id === value).length).length) &&
          !this.allowExpand,
      selection: this.selectionToModel(this.modelValue),
      expanded: this.defaultExpanded,
    };
  },

  computed: {
    optionsDisabled() {
      return (this.options || []).filter(option => option.disabled).map(option => option.id);
    },

    filterOptions() {
      return this.allowExpand && !this.expanded && this.options.length > this.expandCount + EXPAND_COLLAPSE_ALLOWANCE ? this.options.slice(0, this.expandCount) : this.options;
    },

    shouldShow() {
      let onlySelected = false;
      if (this.options && this.options.length === 1) {
        const [option] = this.options;
        onlySelected = Boolean(this.selection[option.id]);
      }

      return this.isCustom || (this.options && this.options.length > 1) || onlySelected;
    },

    allowExpandValues() {
      return this.allowExpand && this.options.length > (this.expandCount + EXPAND_COLLAPSE_ALLOWANCE);
    },
  },

  watch: {
    modelValue: {
      handler(selectionList) {
        this.selection = this.selectionToModel(selectionList);
      },
    },
  },

  mounted() {
    if (this.allowCollapse) {
      this.$smoothReflow({
        el: this.$refs.filterGroupOptions,
      });
    }

    if (this.title && this.title.toLowerCase() === 'product' && this.modelValue?.length > 0) {
      this.collapsed = false;
    }
  },

  methods: {
    toggleCollapse() {
      if (this.allowCollapse) {
        this.collapsed = !this.collapsed;
      } else {
        this.collapsed = false;
      }
    },

    selectionToModel(selectionList) {
      const selection = {};

      if (this.options) {
        this.options.forEach((option) => (selection[option.id] = false));
      }

      if (selectionList) {
        selectionList.forEach((selectedItem) => (selection[selectedItem] = true));
      }

      return selection;
    },

    modelToSelection(selection) {
      return Object.keys(selection)
        .filter((key, index) => selection[key])
        .map((key) => (Number.isNaN(Number(key)) ? key : parseInt(key, 10)));
    },

    filterChanged(value, id) {
      this.selection[id] = value;
      this.$emit('update:modelValue', this.modelToSelection(this.selection));
    },

    onProductValuesExpandClick() {
      this.expanded = !this.expanded;
    },

    getPluralized(text) {
      return naivePluralize(text);
    },
  },
};
</script>
<style lang="postcss" scoped>
@import '@/pixel/style/variables';

.filter-group {
  .filter-group-heading {
    cursor: default;
    display: flex;
    align-items: center;
    justify-content: space-between;
    -webkit-tap-highlight-color: rgba(255, 255, 255, 0);
  }

  .filter-group-heading-text {
    font-family: var(--font-bold);
  }

  .filter-group-options-container {
    @media (max-width: calc($tablet-breakpoint - 1px)) {
      overflow: hidden;
      &.product-values-expanded {
        border-bottom: 1px solid var(--border-color);
      }
    }

    @media (min-width: $tablet-breakpoint) {
      margin-left: calc(-1 * var(--space-3));
    }
  }

  .filter-group-options {
    height: auto;
    transition: height 200ms ease-out;
  }

  .checkbox:first-child {
    margin-top: var(--space-1);
  }

  .checkbox:last-child {
    margin-bottom: var(--space-1);
  }

  .filter-group-heading-plus,
  .filter-group-heading-minus {
    font-size: var(--font-size-14);
  }

  @media (max-width: calc($tablet-breakpoint - 1px)) {
    margin-bottom: var(--space-1);

    .checkbox {
      margin-left: var(--space-1);
      margin-right: var(--space-4);
    }

    .filter-group-options-collapsed {
      height: 0px;
    }

    .filter-group-heading {
      cursor: pointer;
      border-bottom: 1px solid var(--border-color);
      padding: var(--space-1) var(--space-4) var(--space-2) var(--space-4);
    }
  }

  .expand-options {
    display: flex;
    align-items: center;
    padding-left: var(--space-3);

    .af {
      margin-right: var(--space-4);
      font-size: var(--font-size-12);
    }

    .text {
      font-size: var(--font-size-14);
      text-decoration: underline;
    }

    @media (max-width: calc($tablet-breakpoint - 1px)) {
      margin-left: 20px;
      margin-bottom: 10px;
    }
  }

  @media (min-width: $desktop-breakpoint) {
    .filter-group-heading {
      margin-bottom: 12px;
    }

    .filter-group-options {
      .checkbox:not(.expand-options) {
        margin-top: -8px;
      }
    }
  }
}
</style>
